import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import MainFooter from '@/components/MainFooter';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Download, ExternalLink, FileText } from 'lucide-react';
import { reports, Report, getCategoryLabel } from '@/data/reports';
import { ImageOptimizer } from '@/components/common/ImageOptimizer';

// Helper function to get category colors
const getCategoryColor = (category: Report['category']) => {
  switch (category) {
    case 'survey':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'research':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'ebooks':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-200';
  }
};

const ReportCard: React.FC<{ report: Report; onNavigate: (path: string) => void }> = ({ report, onNavigate }) => {
  return (
    <Card 
      className="group cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border border-gray-200"
      onClick={() => onNavigate(report.ctaLink)}
    >
      <CardContent className="p-0">
        {/* Report Image */}
        <div className="relative h-48 overflow-hidden rounded-t-lg">
          {report.image ? (
            <ImageOptimizer
              src={report.image}
              alt={report.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              width={400}
              height={192}
              loading="lazy"
              objectFit="cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/20 to-purple-500/20" />
          )}
          
          {/* Fallback overlay when no image */}
          {!report.image && (
            <div className="absolute inset-0 flex items-center justify-center">
              <FileText className="h-16 w-16 text-brand-blue/30" />
            </div>
          )}
          
          {report.featured && (
            <Badge className="absolute top-3 right-3 bg-brand-blue text-white border-0">
              Featured
            </Badge>
          )}
        </div>

        {/* Report Content */}
        <div className="p-6">
          {/* Category Badge */}
          <Badge 
            variant="outline" 
            className={`mb-3 ${getCategoryColor(report.category)} border`}
          >
            {getCategoryLabel(report.category)}
          </Badge>

          {/* Title */}
          <h3 className="text-lg font-semibold text-brand-darkgrey mb-2 line-clamp-2 group-hover:text-brand-blue transition-colors">
            {report.title}
          </h3>

          {/* Summary */}
          <p className="text-brand-grey text-sm mb-4 line-clamp-3">
            {report.summary || report.longDescription}
          </p>

          {/* Date */}
          <div className="flex items-center gap-2 mb-4 text-sm text-brand-darkgrey">
            <Calendar className="h-4 w-4" />
            <span>{new Date(report.date).toLocaleDateString('en-US', { 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>

          {/* Tags */}
          {report.tags && report.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-4">
              {report.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* CTA Button */}
          <Button 
            className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(report.ctaLink);
            }}
          >
            {report.ctaText}
            <ExternalLink className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Reports = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<Report['category'] | 'all'>('all');
  
  useEffect(() => {
    document.title = "Reports - Cutshort";
  }, []);

  const filteredReports = selectedCategory === 'all' 
    ? reports 
    : reports.filter(report => report.category === selectedCategory);

  const categories: Array<{ value: Report['category'] | 'all'; label: string }> = [
    { value: 'all', label: 'All Reports' },
    { value: 'survey', label: 'Survey' },
    { value: 'research', label: 'Research' },
    { value: 'ebooks', label: 'Ebooks' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <NavBar />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-brand-blue/5 to-purple-500/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-brand-darkgrey mb-6">
              Research & Insights
            </h1>
            <p className="text-xl text-brand-grey mb-8 max-w-2xl mx-auto">
              Data-driven reports and analysis on recruitment trends, hiring challenges, and industry insights to help you make informed decisions.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map((category) => (
              <Button
                key={category.value}
                variant={selectedCategory === category.value ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.value)}
                className={`transition-all ${
                  selectedCategory === category.value
                    ? 'bg-brand-blue text-white'
                    : 'text-brand-darkgrey hover:bg-brand-blue hover:text-white'
                }`}
              >
                {category.label}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Reports Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {filteredReports.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredReports.map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  onNavigate={navigate}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No reports found</h3>
              <p className="text-gray-500">Try selecting a different category.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-brand-blue to-purple-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Stay updated with our latest reports and insights
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Get notified when we publish new reports and insights.
          </p>
          <a 
            href="https://share.hsforms.com/1rL6Atp5ZT5KzgN7iBKLwkAbpcum" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-brand-blue px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
          >
            Subscribe to Newsletter
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </section>

      <MainFooter />
    </div>
  );
};

export default Reports;
